import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { cn, formatDateTime } from "@hostdock/ui";
import { api } from "../api";

const TONE = { INFO: "bg-accent", SUCCESS: "bg-success", WARNING: "bg-warning", ERROR: "bg-danger" } as const;
const LINK: Record<string, (id: string) => string> = { migration: (id) => `/migrate/${id}`, website: (id) => `/websites/${id}` };

export function NotificationBell() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const q = useQuery({ queryKey: ["notifications"], queryFn: () => api.notifications.list({ limit: 20 }), refetchInterval: 30_000 });
  const read = useMutation({ mutationFn: (ids?: string[]) => api.notifications.markRead(ids), onSuccess: () => void qc.invalidateQueries({ queryKey: ["notifications"] }) });
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (!box.current?.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", close); document.removeEventListener("keydown", esc); };
  }, [open]);
  const unread = q.data?.unread ?? 0;
  return (
    <div className="relative" ref={box}>
      <button type="button" aria-label={`Notifications${unread ? `, ${unread} unread` : ""}`} aria-expanded={open} onClick={() => setOpen(!open)} className="relative flex h-9 w-9 items-center justify-center rounded-lg text-fg/85 transition-colors hover:bg-surface-2 hover:text-fg">
        <Bell size={20} strokeWidth={1.8} />
        {unread > 0 && <span className="absolute -right-1 -top-1 flex h-[18px] min-w-[18px] ring-2 ring-surface items-center justify-center rounded-full bg-danger px-1 text-[10px] font-semibold text-white">{unread > 9 ? "9+" : unread}</span>}
      </button>
      {open && (
        <div role="dialog" aria-label="Notifications" className="hd-enter absolute right-0 z-30 mt-2 w-[min(24rem,calc(100vw-2rem))] overflow-hidden rounded-2xl border border-line bg-surface shadow-lg">
          <div className="flex items-center justify-between border-b border-line px-4 py-3">
            <p className="text-[13px] font-semibold">Notifications</p>
            {unread > 0 && <button type="button" className="text-[12px] text-accent hover:underline" onClick={() => read.mutate(undefined)}>Mark all read</button>}
          </div>
          <ul className="max-h-96 overflow-y-auto">
            {q.data?.items.length === 0 && <li className="px-3 py-8 text-center text-[13px] text-muted">Nothing yet. Important events about your hosting will show up here.</li>}
            {q.data?.items.map((n) => (
              <li key={n.id}>
                <button
                  type="button"
                  className={cn("flex w-full gap-3 px-3 py-2.5 text-left hover:bg-surface-2", !n.read && "bg-accent-soft/40")}
                  onClick={() => { if (!n.read) read.mutate([n.id]); const to = n.resourceType && n.resourceId ? LINK[n.resourceType]?.(n.resourceId) : undefined; if (to) { setOpen(false); navigate(to); } }}
                >
                  <span className={cn("mt-1.5 h-2 w-2 shrink-0 rounded-full", TONE[n.severity])} aria-hidden />
                  <span className="min-w-0">
                    <span className="block truncate text-[13px] font-medium">{n.title}</span>
                    {n.body && <span className="line-clamp-2 block text-[12px] text-muted">{n.body}</span>}
                    <span className="block text-[11px] text-subtle">{formatDateTime(n.createdAt)}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
